import { LIABILITY_TYPE_LABELS, type UserLiability } from "@/services/liabilities/types";

export interface ProcessedLiability {
  id: string;
  name: string;
  category: string;
  amount: number;
  user_id: string;
  liability_type: UserLiability["liability_type"];
  lender?: string;
  interest_rate?: number;
}

function parseLiabilityAmount(value: number | string | undefined): number {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
}

/**
 * Process Liabilities To Detailed.
 */
export function processLiabilitiesToDetailed(
  liabilities: UserLiability[] | null | undefined
): ProcessedLiability[] {
  return (liabilities ?? []).map((liability) => ({
    id: liability.id,
    name: liability.name,
    category: LIABILITY_TYPE_LABELS[liability.liability_type] ?? "Other Debt",
    amount: parseLiabilityAmount(liability.outstanding_balance),
    user_id: liability.user_id,
    liability_type: liability.liability_type,
    lender: liability.lender ?? undefined,
    interest_rate: liability.interest_rate ?? undefined,
  }));
}

/**
 * Extract Embedded Real Estate Loans.
 */
export function extractEmbeddedRealEstateLoans(
  realEstateAssets: Array<{
    id: string;
    user_id: string;
    name?: string;
    metadata?: Record<string, unknown>;
  }>
): ProcessedLiability[] {
  const embedded: ProcessedLiability[] = [];

  realEstateAssets.forEach((asset) => {
    const metadata = asset.metadata ?? {};
    const rawLoan =
      metadata.loan_amount ??
      metadata.outstanding_loan ??
      metadata.property_loan ??
      metadata.loan_balance;
    const amount = parseLiabilityAmount(rawLoan as number | string | undefined);

    if (amount <= 0) {
      return;
    }

    embedded.push({
      id: `embedded-loan-${asset.id}`,
      name: `${asset.name || "Property"} Loan`,
      category: "Home Loan (Property)",
      amount,
      user_id: asset.user_id,
      liability_type: "home_loan",
    });
  });

  return embedded;
}

export function mergeLiabilities(
  explicit: ProcessedLiability[],
  embedded: ProcessedLiability[]
): ProcessedLiability[] {
  return [...explicit, ...embedded];
}
